import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  IconButton,
  Chip,
} from "@mui/material";
import {
  UnfoldMore,
  Add as AddIcon,
  Check as CheckIcon,
  Business as OrgIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { fetchOrganizations, setCurrentOrg } from "../../redux/slices/orgSlice";

export default function OrgSwitcher() {
  const dispatch = useDispatch();
  const { organizations, currentOrg } = useSelector((state) => state.org);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch(fetchOrganizations());
  }, [dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectOrg = (org) => {
    dispatch(setCurrentOrg(org));
    handleClose();
    // Reload data for the new org context
    window.location.reload();
  };

  if (!currentOrg) return null;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          mx: 1,
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          transition: "background-color 0.15s",
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: currentOrg.isPersonal ? "primary.main" : "secondary.main",
            fontSize: "0.75rem",
          }}
          src={currentOrg.avatarUrl || undefined}
        >
          {currentOrg.isPersonal ? (
            <PersonIcon sx={{ fontSize: "1rem" }} />
          ) : (
            currentOrg.name?.[0]?.toUpperCase()
          )}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentOrg.name}
          </Typography>
          {currentOrg.role && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.625rem", textTransform: "capitalize" }}
            >
              {currentOrg.role.toLowerCase()}
            </Typography>
          )}
        </Box>
        <UnfoldMore sx={{ fontSize: "1rem", color: "text.secondary" }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 240,
            maxHeight: 360,
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Organizations
          </Typography>
        </Box>

        {organizations.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => handleSelectOrg(org)}
            selected={org.id === currentOrg?.id}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: org.isPersonal ? "primary.light" : "secondary.light",
                  fontSize: "0.625rem",
                }}
                src={org.avatarUrl || undefined}
              >
                {org.isPersonal ? (
                  <PersonIcon sx={{ fontSize: "0.875rem" }} />
                ) : (
                  org.name?.[0]?.toUpperCase()
                )}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={org.name}
              secondary={org.role?.toLowerCase()}
              primaryTypographyProps={{ fontSize: "0.8125rem" }}
              secondaryTypographyProps={{ fontSize: "0.6875rem" }}
            />
            {org.id === currentOrg?.id && (
              <CheckIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
            )}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            handleClose();
            window.location.href = "/dashboard/org/settings?create=true";
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create Organization"
            primaryTypographyProps={{ fontSize: "0.8125rem" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
